import { useEffect, useState } from 'react';
import { getSiteSettings, updateSiteSettings } from '../../services/apiClient';
import type { SiteSettings } from '../../types';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import styles from './SiteSettings.module.css';

export function SiteSettings() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [form, setForm] = useState<Partial<SiteSettings>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getSiteSettings().then((r) => {
      if (r.data) {
        setSettings(r.data);
        setForm(r.data);
      }
    });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('social_')) {
      const key = name.replace('social_', '');
      setForm((prev) => ({
        ...prev,
        social_links: { ...prev.social_links, [key]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    setSaved(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const res = await updateSiteSettings(form);
    if (res.data) setSettings(res.data);
    setSaving(false);
    setSaved(true);
  };

  if (!settings || !form) return <p className={styles.loading}>Loading settings…</p>;

  return (
    <div className={styles.page}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <Card padding="md">
          <h2 className={styles.sectionTitle}>Organization Display</h2>
          <div className={styles.fields}>
            <Field label="Display Name" name="display_name" value={form.display_name ?? ''} onChange={handleChange} />
            <Field label="Logo URL" name="logo_url" value={form.logo_url ?? ''} onChange={handleChange} placeholder="https://..." />
            <Field label="Primary Color" name="primary_color" value={form.primary_color ?? ''} onChange={handleChange} placeholder="#1a3a5c" type="color" />
            <Field label="Secondary Color" name="secondary_color" value={form.secondary_color ?? ''} onChange={handleChange} placeholder="#c8922a" type="color" />
            <Field label="Default CTA Text" name="default_cta_text" value={form.default_cta_text ?? ''} onChange={handleChange} />
          </div>
        </Card>

        <Card padding="md">
          <h2 className={styles.sectionTitle}>Contact Information</h2>
          <div className={styles.fields}>
            <Field label="Contact Email" name="contact_email" value={form.contact_email ?? ''} onChange={handleChange} type="email" />
            <Field label="Phone" name="phone" value={form.phone ?? ''} onChange={handleChange} type="tel" />
            <Field label="Address" name="address" value={form.address ?? ''} onChange={handleChange} />
          </div>
        </Card>

        <Card padding="md">
          <h2 className={styles.sectionTitle}>Social Links</h2>
          <div className={styles.fields}>
            <Field label="Facebook URL" name="social_facebook" value={form.social_links?.facebook ?? ''} onChange={handleChange} placeholder="https://facebook.com/..." />
            <Field label="Twitter / X URL" name="social_twitter" value={form.social_links?.twitter ?? ''} onChange={handleChange} placeholder="https://twitter.com/..." />
            <Field label="Instagram URL" name="social_instagram" value={form.social_links?.instagram ?? ''} onChange={handleChange} placeholder="https://instagram.com/..." />
          </div>
        </Card>

        <Card padding="md">
          <h2 className={styles.sectionTitle}>Footer</h2>
          <div className={styles.fields}>
            <div className={styles.field}>
              <label className={styles.label}>Footer Text</label>
              <textarea
                name="footer_text"
                value={form.footer_text ?? ''}
                onChange={handleChange}
                rows={3}
                className={styles.textarea}
              />
            </div>
          </div>
        </Card>

        <div className={styles.actions}>
          {saved && <span className={styles.savedMsg}>Settings saved.</span>}
          <Button type="submit" loading={saving}>Save Settings</Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className={styles.field}>
      <label htmlFor={name} className={styles.label}>{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={styles.input}
      />
    </div>
  );
}
